import React, { useCallback, useMemo, useState } from 'react';
import {
  Form,
  Input,
  Modal,
  Button,
  Spin,
  Switch,
  Tree,
  Tooltip,
  Popover,
  Empty
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { SimpleCampaignItemDto, SimpleFolderItemDto } from '@api/Api';
import { useCurrentWorkspace } from '@hooks/workspace';
import {
  toggleCampaignFolderCreationModal,
  toggleCopyModal
} from '@redux/actions/modalAction';
import {
  useCopyAssetsMutation,
  useCopyFolderMutation
} from '@hooks/queries/assets';
import { useSimpleCampaignsListQuery } from '@hooks/queries/campaign';
import { useFolderTreeQueries } from '@hooks/queries/folders';
import { CopyModalStateProps } from '@redux/types/modalType';
import {
  ArrowDownIcon,
  CampaingIcon,
  CrossIcon,
  EyePrivateIcon,
  FolderIcon,
  FolderPrivateIcon,
  ImgSecondaryIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon
} from '@icons';
import './CopyModal.less';

type FormValues = {
  selectedCampaignId: string;
  selectedFolderId: string | null;
  searchText: string;
  copyComments: boolean;
  copyAssets: boolean;
  includeMembers: boolean;
  selectedCampaignName: string | null;
  selectedFolderName: string | null;
  selectedFolderIsPrivate: boolean;
};

function copyOptionsDescription(
  copyAssets: boolean,
  copyComments: boolean,
  includeMembers: boolean
): string {
  const parts: string[] = [];
  if (copyAssets) parts.push('media');
  if (copyComments) parts.push('comments');
  if (includeMembers) parts.push('members');

  if (parts.length === 0) return 'Only folder';
  if (parts.length === 1) return `Folder with ${parts[0]}`;

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');
  return `Folder with ${rest} and ${last}`;
}

export default function CopyModal(props: CopyModalStateProps) {
  const { visible, type } = props;
  const isFolder = type === 'folder';
  const folderId = isFolder ? props.folderId : undefined;
  const folderName = isFolder ? props.name : undefined;
  const folderIsPrivate = isFolder ? props.isPrivate : undefined;
  const assets = !isFolder ? props.assets : undefined;

  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();
  const canCreateCampaign = currentWorkspace?.permissions?.createCampaigns;
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const searchText = Form.useWatch('searchText', form) ?? '';
  const selectedCampaignId = Form.useWatch('selectedCampaignId', form) ?? '';
  const selectedFolderId = Form.useWatch('selectedFolderId', form) ?? '';
  const copyAssets = Form.useWatch('copyAssets', form) ?? true;
  const copyComments = Form.useWatch('copyComments', form) ?? true;
  const includeMembers = Form.useWatch('includeMembers', form) ?? true;
  const selectedCampaignName =
    Form.useWatch('selectedCampaignName', form) ?? '';
  const selectedFolderName = Form.useWatch('selectedFolderName', form) ?? '';
  const selectedFolderIsPrivate =
    Form.useWatch('selectedFolderIsPrivate', form) ?? false;

  const copyOptionsText = useMemo(
    () =>
      copyOptionsDescription(!!copyAssets, !!copyComments, !!includeMembers),
    [copyAssets, copyComments, includeMembers]
  );

  const [expanded, setExpanded] = useState<
    { campaignId: string; folderId?: string }[]
  >([]);

  const { data: campaignsData } = useSimpleCampaignsListQuery({
    params: { workspaceId: currentWorkspace?.id || '' },
    enabled: visible && !!currentWorkspace?.id
  });

  const folderQueries = useFolderTreeQueries(
    { workspaceId: currentWorkspace?.id || '', expanded },
    { enabled: visible && !!currentWorkspace?.id }
  );

  const onCreateFolder = useCallback(
    (campaignId: string, parentId?: string) => {
      if (!campaignId) return;
      dispatch(
        toggleCampaignFolderCreationModal({
          visible: true,
          entity: 'folder',
          campaignId,
          parentId
        })
      );
    },
    [dispatch]
  );

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        notShowCampaignCreatedModal: true
      })
    );
  }, [dispatch, canCreateCampaign]);

  const filteredCampaigns = useMemo(() => {
    let list: SimpleCampaignItemDto[] = campaignsData ?? [];
    if (searchText) {
      list = list.filter((c) =>
        c.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
      );
    }
    return list;
  }, [campaignsData, searchText]);

  const treeData = filteredCampaigns.map((item: SimpleCampaignItemDto) => {
    function getChildren(
      campaignId: string,
      parentFolderId?: string
    ): { isLoading: boolean; data: object[] } | undefined {
      const queryIndex = expanded.findIndex(
        (e) => e.campaignId === campaignId && e.folderId === parentFolderId
      );
      if (queryIndex === -1) return undefined;
      const query = folderQueries[queryIndex];
      return {
        isLoading: query.isLoading,
        data:
          query.data?.map((folder: SimpleFolderItemDto) => {
            const children = getChildren(campaignId, folder.id);
            return {
              icon: folder.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />,
              key: folder.id,
              folderId: folder.id,
              folderName: folder.name,
              campaignId,
              isPrivate: folder.isPrivate,
              isLeaf: false,
              switcherIcon: children?.isLoading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
                />
              ) : undefined,
              title: (
                <span className="copy-modal__tree-title">
                  <span className="copy-modal__tree-title-text">
                    {folder.name}
                  </span>
                  <Tooltip
                    title="Create subfolder"
                    overlayClassName="toggle_favorite_overlay"
                  >
                    <button
                      type="button"
                      className="copy-modal__create-folder-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateFolder(campaignId, folder.id);
                      }}
                    >
                      <PlusIcon />
                    </button>
                  </Tooltip>
                </span>
              ),
              children: children?.data
            };
          }) ?? []
      };
    }
    const children = getChildren(item.id);
    return {
      key: item.id,
      icon: <CampaingIcon />,
      title: (
        <span className="copy-modal__tree-title">
          <span className="copy-modal__tree-title-text">
            {item.name}
            {item.isPrivate && <EyePrivateIcon />}
          </span>
          <Tooltip
            title="Create folder"
            overlayClassName="toggle_favorite_overlay"
          >
            <button
              type="button"
              className="copy-modal__create-folder-btn"
              onClick={(e) => {
                e.stopPropagation();
                onCreateFolder(item.id);
              }}
            >
              <PlusIcon />
            </button>
          </Tooltip>
        </span>
      ),
      campaignName: item.name,
      isLeaf: false,
      switcherIcon: children?.isLoading ? (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
      ) : undefined,
      children: children?.data
    };
  });

  const { mutateAsync: copyAssetsMutation, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});
  const { mutateAsync: copyFolder, isPending: isCopyingFolder } =
    useCopyFolderMutation({});

  const isPending = isFolder ? isCopyingFolder : isCopyingAssets;

  const onCloseModal = useCallback(() => {
    dispatch(toggleCopyModal(null));
  }, [dispatch]);

  const onSubmit = (data: FormValues) => {
    if (isFolder && folderId) {
      copyFolder(
        {
          workspaceId: currentWorkspace?.id || '',
          folderId,
          targetCampaignId: data.selectedCampaignId,
          targetFolderId: data.selectedFolderId ?? undefined,
          copyAssets: data.copyAssets,
          copyComments: data.copyComments,
          includeMembers: data.includeMembers
        },
        {
          onSuccess: (res) => {
            if (res.data.jobId) {
              localStorage.setItem(
                `copy-link-${res.data.jobId}`,
                `/campaigns/single/${data.selectedCampaignId}/${res.data.newFolderId}`
              );
            }
            onCloseModal();
          }
        }
      );
    } else if (!isFolder && assets) {
      copyAssetsMutation(
        {
          workspaceId: currentWorkspace?.id || '',
          assetIds: assets.map((a) => a.id),
          targetCampaignId: data.selectedCampaignId,
          targetFolderId: data.selectedFolderId ?? undefined,
          copyComments: data.copyComments
        },
        {
          onSuccess: (res) => {
            if (res.data.jobId) {
              localStorage.setItem(
                `copy-link-${res.data.jobId}`,
                `/campaigns/single/${data.selectedCampaignId}/${
                  data.selectedFolderId ?? ''
                }`
              );
            }
            onCloseModal();
          }
        }
      );
    }
  };

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossIcon />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onSubmit}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          copyAssets: true,
          copyComments: true,
          includeMembers: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 8 }}
        >
          Copy to
        </h2>
        {isFolder ? (
          <>
            {!selectedCampaignName && !selectedFolderName && (
              <div className="copy-modal__top">
                Select a destination to copy
                <div className="copy-modal__name">
                  {folderIsPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={folderName}
                  >
                    <div className="copy-modal__name-text">{folderName}</div>
                  </Tooltip>
                </div>
              </div>
            )}
            {(!!selectedCampaignName || !!selectedFolderName) && (
              <div className="copy-modal__top">
                Copy
                <div className="copy-modal__name">
                  {folderIsPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={folderName}
                  >
                    <div className="copy-modal__name-text">{folderName}</div>
                  </Tooltip>
                </div>
                into
                <div className="copy-modal__name">
                  {selectedFolderName ? (
                    <>
                      {selectedFolderIsPrivate ? (
                        <FolderPrivateIcon />
                      ) : (
                        <FolderIcon />
                      )}
                      <Tooltip
                        placement="top"
                        overlayClassName="toggle_favorite_overlay"
                        title={selectedFolderName}
                      >
                        <div className="copy-modal__name-text">
                          {selectedFolderName}
                        </div>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <CampaingIcon />
                      <Tooltip
                        placement="top"
                        overlayClassName="toggle_favorite_overlay"
                        title={selectedCampaignName}
                      >
                        <div className="copy-modal__name-text">
                          {selectedCampaignName}
                        </div>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {!selectedCampaignName && !selectedFolderName && (
              <div className="copy-modal__top">
                Select a destination to copy
                {assets && assets.length === 1 ? (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <Tooltip
                      placement="top"
                      overlayClassName="toggle_favorite_overlay"
                      title={assets[0].name}
                    >
                      <div className="copy-modal__name-text">
                        {assets[0].name}
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <div className="copy-modal__name-text">
                      {assets?.length ?? 0} items
                    </div>
                  </div>
                )}
              </div>
            )}
            {!!selectedCampaignName && (
              <div className="copy-modal__top">
                Copy
                {assets && assets.length === 1 ? (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <Tooltip
                      placement="top"
                      overlayClassName="toggle_favorite_overlay"
                      title={assets[0].name}
                    >
                      <div className="copy-modal__name-text">
                        {assets[0].name}
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <div className="copy-modal__name-text">
                      {assets?.length ?? 0} items
                    </div>
                  </div>
                )}
                into
                <div className="copy-modal__name">
                  <CampaingIcon />
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={selectedCampaignName}
                  >
                    <div className="copy-modal__name-text">
                      {selectedCampaignName}
                    </div>
                  </Tooltip>
                </div>
              </div>
            )}
            {!!selectedFolderName && (
              <div className="copy-modal__top">
                Copy
                {assets && assets.length === 1 ? (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <Tooltip
                      placement="top"
                      overlayClassName="toggle_favorite_overlay"
                      title={assets[0].name}
                    >
                      <div className="copy-modal__name-text">
                        {assets[0].name}
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="copy-modal__name">
                    <ImgSecondaryIcon />
                    <div className="copy-modal__name-text">
                      {assets?.length ?? 0} items
                    </div>
                  </div>
                )}
                into
                <div className="copy-modal__name">
                  {selectedFolderIsPrivate ? (
                    <FolderPrivateIcon />
                  ) : (
                    <FolderIcon />
                  )}
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={selectedFolderName}
                  >
                    <div className="copy-modal__name-text">
                      {selectedFolderName}
                    </div>
                  </Tooltip>
                </div>
              </div>
            )}
          </>
        )}
        <Form.Item className="copy-modal__search-input">
          <Form.Item name="searchText" noStyle>
            <Input
              placeholder="Search by project"
              prefix={<SearchIcon />}
              size="large"
            />
          </Form.Item>
          {canCreateCampaign && (
            <Button
              htmlType="button"
              type="ghost"
              className="copy-modal__action-button"
              onClick={onCreateProject}
              icon={<PlusIcon />}
            >
              New project
            </Button>
          )}
        </Form.Item>
        <div className="copy-modal__tree-wrapper">
          {treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing found"
            />
          ) : (
            <Tree
              treeData={treeData}
              showIcon
              showLine
              selectedKeys={
                selectedFolderId ? [selectedFolderId] : [selectedCampaignId]
              }
              expandedKeys={expanded.map((x) =>
                x.folderId ? x.folderId : x.campaignId
              )}
              onExpand={(_keys, { expanded: isExpanding, node }) => {
                const n = node as any;
                const hasFolderId = 'folderId' in n;
                if (isExpanding) {
                  if (hasFolderId) {
                    setExpanded((prev) => [
                      ...prev,
                      { campaignId: n.campaignId, folderId: n.folderId }
                    ]);
                  } else {
                    setExpanded((prev) => [
                      ...prev,
                      { campaignId: n.key as string }
                    ]);
                  }
                } else {
                  if (hasFolderId) {
                    setExpanded((prev) =>
                      prev.filter(
                        (e) =>
                          !(
                            e.campaignId === n.campaignId &&
                            e.folderId === n.folderId
                          )
                      )
                    );
                  } else {
                    setExpanded((prev) =>
                      prev.filter((e) => e.campaignId !== (n.key as string))
                    );
                  }
                }
              }}
              onSelect={(_selectedKeys, { node }) => {
                const n = node as any;
                const hasCampaignId = 'campaignId' in n;
                const hasIsPrivate = 'isPrivate' in n;
                if (n.selected) {
                  form.setFieldsValue({
                    selectedCampaignId: '',
                    selectedFolderId: '',
                    selectedCampaignName: null,
                    selectedFolderName: null,
                    selectedFolderIsPrivate: false
                  });
                  return;
                }
                if (hasCampaignId) {
                  form.setFieldsValue({
                    selectedCampaignId: n.campaignId,
                    selectedFolderId: n.key,
                    selectedFolderName: n.folderName as string,
                    selectedFolderIsPrivate: hasIsPrivate
                      ? (n.isPrivate as boolean)
                      : false,
                    selectedCampaignName: null
                  });
                  return;
                }
                form.setFieldsValue({
                  selectedCampaignId: n.key,
                  selectedCampaignName: n.campaignName,
                  selectedFolderId: null,
                  selectedFolderName: null,
                  selectedFolderIsPrivate: false
                });
              }}
              switcherIcon={<ArrowDownIcon />}
            />
          )}
        </div>
        <Form.Item name="selectedCampaignId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="selectedFolderId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="selectedCampaignName" hidden />
        <Form.Item name="selectedFolderName" hidden />
        <Form.Item name="selectedFolderIsPrivate" hidden />
        {isFolder && (
          <>
            <Form.Item name="copyAssets" valuePropName="checked" hidden />
            <Form.Item name="copyComments" valuePropName="checked" hidden />
            <Form.Item name="includeMembers" valuePropName="checked" hidden />
            <div
              className="copy-modal__options"
              onMouseEnter={() => setIsOptionsOpen(true)}
              onMouseLeave={() => setIsOptionsOpen(false)}
            >
              <div className="copy-modal__options-description">
                <div className="copy-modal__options-title">Copy options</div>
                <div className="copy-modal__options-text">
                  {copyOptionsText}
                </div>
              </div>
              <Popover
                placement="topRight"
                getPopupContainer={(e) => e.parentElement as HTMLElement}
                content={
                  <div>
                    <div className="copy-modal__options-title">
                      Copy options
                    </div>
                    <div
                      className="copy-modal__switch"
                      onClick={() =>
                        form.setFieldValue('copyAssets', !copyAssets)
                      }
                    >
                      <Switch checked={!!copyAssets} />
                      <span>Duplicate media</span>
                    </div>
                    <div
                      className="copy-modal__switch"
                      onClick={() =>
                        form.setFieldValue('copyComments', !copyComments)
                      }
                    >
                      <Switch checked={!!copyComments} />
                      <span>Duplicate comments</span>
                    </div>
                    <div
                      className="copy-modal__switch"
                      onClick={() =>
                        form.setFieldValue('includeMembers', !includeMembers)
                      }
                    >
                      <Switch checked={!!includeMembers} />
                      <span>Include members</span>
                    </div>
                  </div>
                }
                trigger="hover"
                showArrow={false}
                open={isOptionsOpen}
              >
                <SettingsIcon />
              </Popover>
            </div>
          </>
        )}
        {!isFolder && (
          <Form.Item name="copyComments" valuePropName="checked" hidden />
        )}
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isPending}
            disabled={!selectedCampaignId}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            {isFolder ? 'Copy folder' : `Copy ${assets?.length ?? ''} items`}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
